import type { CanvasNode } from '@/types/visual-editor';
import { useVisualEditorStore } from '@/stores/visualEditorStore';
import {
  createCanvasActivityTimestamp,
  ensureEnteredCanvasAt,
  stampCanvasNodeActivity,
} from './canvas-node-entry';
import { getNextNodePosition, getRecentActivityClusterOrigin, type Position } from './node-placement';
import { getWorkflowPlacementExcludedRects } from './workflow-area-snapshot';

interface ExternalCanvasPlacementOptions {
  existingNodes?: CanvasNode[];
  preferredOrigin?: Position | null;
  itemWidth?: number;
  itemHeight?: number;
  activityAt?: string;
  preserveProvidedPositions?: boolean;
}

interface AddExternalCanvasNodesOptions extends ExternalCanvasPlacementOptions {
  focus?: boolean;
  persist?: boolean;
  strictPersistence?: boolean;
  historyLabel?: string;
}

export function placeExternalCanvasNodes(
  nodes: CanvasNode[],
  options: ExternalCanvasPlacementOptions = {},
): { nodes: CanvasNode[]; activityAt: string; preferredOrigin: Position | null } {
  if (nodes.length === 0) {
    return {
      nodes: [],
      activityAt: options.activityAt ?? createCanvasActivityTimestamp(),
      preferredOrigin: options.preferredOrigin ?? null,
    };
  }

  const editorState = useVisualEditorStore.getState();
  const existingNodes = options.existingNodes ?? editorState.nodes;
  const activityAt = options.activityAt ?? createCanvasActivityTimestamp();
  const fallbackMs = Number.isFinite(Date.parse(activityAt)) ? Date.parse(activityAt) : Date.now();
  const preferredOrigin = options.preferredOrigin === undefined
    ? getRecentActivityClusterOrigin(existingNodes)
    : options.preferredOrigin;
  const excludedRects = getWorkflowPlacementExcludedRects(editorState.nodes, editorState.canvasEdges);

  const pendingPositions = existingNodes
    .filter((node) => node.position)
    .map((node) => ({ position: node.position }));

  const placedNodes = nodes.map((node) => {
    const shouldReusePosition = options.preserveProvidedPositions === true && !!node.position;
    const position = shouldReusePosition
      ? node.position
      : getNextNodePosition({
          existingNodes: pendingPositions,
          preferredOrigin,
          itemWidth: options.itemWidth,
          itemHeight: options.itemHeight,
          excludedRects,
        });

    pendingPositions.push({ position });

    const withEntry = ensureEnteredCanvasAt({
      ...node,
      position,
      enteredCanvasAt: node.enteredCanvasAt ?? activityAt,
    }, fallbackMs);

    const withActivity = node.lastActivityAt
      ? withEntry
      : stampCanvasNodeActivity(withEntry, activityAt);

    return withActivity;
  });

  return {
    nodes: placedNodes,
    activityAt,
    preferredOrigin,
  };
}

export async function addExternalNodesToCurrentCanvas(
  nodes: CanvasNode[],
  options: AddExternalCanvasNodesOptions = {},
): Promise<CanvasNode[]> {
  const placed = placeExternalCanvasNodes(nodes, options);
  await useVisualEditorStore.getState().addNodes(placed.nodes, {
    focus: options.focus,
    persist: options.persist,
    strictPersistence: options.strictPersistence,
    historyLabel: options.historyLabel,
  });
  return placed.nodes;
}